import React, { useState } from "react";
import { Check, CheckCircle2, Copy, Gift, Loader2, Smartphone } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { VIPPS_NUMBER, fmtVipps, priceOf } from "@/config/supporter";

const Row = ({ label, value, strong }) => (
  <div className={`flex justify-between gap-4 ${strong ? "pt-2 mt-2 border-t border-current/20 font-extrabold" : ""}`}>
    <dt>{label}</dt>
    <dd className="tabular-nums">{value}</dd>
  </div>
);

export const OrderSummary = ({ pkg, placed, sending, error }) => {
  const { t, fmtNum } = useLang();
  const s = t.pages.signup;
  const sp = t.pages.supporter;
  const free = pkg?.free;
  const price = pkg && !free && priceOf(pkg);
  return (
    <div data-testid="signup-summary" className="rounded-[1.75rem] bg-ink text-parchment p-7 md:p-8 shadow-journal-lg">
      <div className="text-xs font-extrabold uppercase tracking-[0.16em] text-amber">{s.summary}</div>
      {pkg ? (
        <>
          <h3 data-testid="signup-summary-package" className="mt-2 font-display text-2xl font-semibold">{free ? s.host.name : sp.packages[pkg.key].name}</h3>
          <p className="mt-1 text-sm text-parchment/70">{(free ? s.placedHost : s.placed).replace("{n}", placed).replace("{max}", pkg.trolls)}</p>
          {free ? (
            <p data-testid="signup-summary-total" className="mt-5 font-display text-4xl font-semibold text-amber">{s.host.free}</p>
          ) : (
            <dl className="mt-5 space-y-1.5 text-sm text-parchment/85">
              <Row label={s.exVat} value={`${fmtNum(price.ex)} kr`} />
              <Row label={s.vat} value={`${fmtNum(price.vat)} kr`} />
              <Row label={s.total} value={<span data-testid="signup-summary-total">{fmtNum(price.total)} kr</span>} strong />
            </dl>
          )}
        </>
      ) : (
        <p className="mt-3 text-parchment/70">{s.pickPackage}</p>
      )}
      <p data-testid="signup-summary-note" className="mt-6 flex gap-3 rounded-2xl bg-parchment/5 border border-parchment/10 p-4 text-sm text-parchment/85">
        {free ? <Gift className="w-5 h-5 shrink-0 text-amber" /> : <Smartphone className="w-5 h-5 shrink-0 text-[#FF5B24]" />} {free ? s.hostNote : s.payNote}
      </p>
      {error && <p role="alert" data-testid="signup-error" className="mt-5 text-sm font-bold text-amber">{error}</p>}
      <button type="submit" disabled={sending} data-testid="signup-submit" className="mt-6 w-full inline-flex justify-center items-center gap-2 rounded-full bg-amber hover:bg-amber-dark disabled:opacity-60 text-parchment px-6 py-4 font-extrabold transition-colors">
        {sending && <Loader2 className="w-4 h-4 animate-spin" />} {sending ? s.sending : free ? s.submitHost : s.submit}
      </button>
    </div>
  );
};

const CopyRow = ({ label, value, display, testId }) => {
  const { t } = useLang();
  const s = t.pages.signup;
  const [ok, setOk] = useState(false);
  const copy = () => navigator.clipboard?.writeText(value).then(() => {
    setOk(true);
    setTimeout(() => setOk(false), 1600);
  });
  return (
    <div className="flex items-center justify-between gap-3 rounded-2xl bg-parchment border border-line px-4 py-3">
      <div>
        <div className="text-xs font-extrabold uppercase tracking-[0.14em] text-ink-muted">{label}</div>
        <div data-testid={testId} className="font-display text-2xl font-semibold text-ink tabular-nums">{display || value}</div>
      </div>
      <button type="button" onClick={copy} data-testid={`${testId}-copy`} className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1.5 text-sm font-bold text-ink hover:border-moss hover:text-moss transition-colors">
        {ok ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} {ok ? s.copied : s.copy}
      </button>
    </div>
  );
};

export const SignupDone = ({ order, onAnother }) => {
  const { t, fmtNum } = useLang();
  const s = t.pages.signup;
  const fill = (str) =>
    str.replace("{name}", order.firstName).replace("{ref}", order.ref).replace("{email}", order.email)
      .replace("{number}", fmtVipps(VIPPS_NUMBER)).replace("{amount}", fmtNum(order.total));
  return (
    <div data-testid="signup-success" className="mt-12 grid lg:grid-cols-12 gap-10 items-start">
      <div className="lg:col-span-5">
        <CheckCircle2 className="w-12 h-12 text-moss" />
        <div className="mt-4 text-xs font-extrabold uppercase tracking-[0.16em] text-amber-dark">{s.doneKicker}</div>
        <h3 data-testid="signup-success-title" className="mt-2 font-display text-3xl font-semibold text-ink">{fill(s.doneTitle)}</h3>
        <p className="mt-4 text-ink-muted leading-relaxed">{fill(order.client_email_sent === false ? s.doneBodyNoMail : s.doneBody)}</p>
        <button type="button" onClick={onAnother} data-testid="signup-another" className="mt-6 rounded-full border border-line px-5 py-2.5 font-bold text-ink hover:border-moss hover:text-moss transition-colors">
          {s.another}
        </button>
      </div>
      <div data-testid="vipps-pay" className="lg:col-span-7 rounded-[1.75rem] border-2 border-[#FF5B24]/40 bg-paper p-6 md:p-8">
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-[#FF5B24] text-white px-3 py-1 text-sm font-extrabold">Vipps</span>
          <h4 className="font-display text-2xl font-semibold text-ink">{s.payTitle}</h4>
        </div>
        <div className="mt-6 grid sm:grid-cols-2 gap-3">
          <CopyRow label={s.number} value={VIPPS_NUMBER} display={fmtVipps(VIPPS_NUMBER)} testId="vipps-number" />
          <CopyRow label={s.total} value={String(order.total)} display={`${fmtNum(order.total)} kr`} testId="vipps-amount" />
          <div className="sm:col-span-2"><CopyRow label={s.ref} value={order.ref} testId="vipps-ref" /></div>
        </div>
        <dl className="mt-5 space-y-1 text-sm text-ink-muted">
          <Row label={s.exVat} value={`${fmtNum(order.price_ex_vat)} kr`} />
          <Row label={s.vat} value={`${fmtNum(order.vat)} kr`} />
        </dl>
        <ol className="mt-6 space-y-2.5">
          {s.steps.map((step, i) => (
            <li key={step} className="flex gap-3 text-ink">
              <span className="w-6 h-6 shrink-0 rounded-full bg-[#FF5B24] text-white text-xs font-extrabold grid place-items-center">{i + 1}</span>
              <span className="font-semibold">{fill(step)}</span>
            </li>
          ))}
        </ol>
        <p className="mt-5 text-sm text-ink-muted">{s.invoice}</p>
      </div>
    </div>
  );
};
